import { HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { contactFormGroupIds } from '../../config/contact-form-topics';
import {
    ContactFormCustomerType,
    ContactFormData,
    ContractTerminationOrRevocationFormData,
    SalesPartnerContactFormData,
} from '../../models/contact-form-data';
import { base64Encode } from '../../utils/base64-encode';
import { HttpService } from '../http/http.service';

const basePath = environment.freshdeskApiPath;

@Injectable({
    providedIn: 'root',
})
export class TicketsystemInterfaceService {
    private http = inject(HttpService);

    public createSalesPartnerContactFormTicket(data: SalesPartnerContactFormData): Observable<unknown> {
        const headers = this.getHeaders();
        const content = {
            subject: 'Nachricht über Kontaktformular Vertriebspartner',
            name: data.name,
            email: data.mail,
            phone: data.phone,
            description: `
                <h1>Nachricht über Kontaktformular Vertriebspartner:</h1>
                <p><strong>Absenderinformationen:</strong></p>
                <p>
                    Name: ${data.name}<br>
                    E-Mail-Adresse: ${data.mail}<br>
                    Tel.: ${data.phone}<br>
                </p>
                <p><strong>Nachricht:</strong><br></p>
                <p>
                    <pre>${data.message}</pre>
                </p>`,
            status: 2,
            priority: 1,
            group_id: contactFormGroupIds.b2b,
            type: 'Kontaktformular Vertriebspartner',
        };
        const body = data.captchaResponse
            ? {
                  data: content,
                  captchaResponse: data.captchaResponse,
              }
            : content;
        return this.http.post(`${basePath}tickets`, body, { headers, notify: 'ALL' });
    }

    // note: if the data sent changes and we still use the php captcha proxy, then proxy
    // might need an update, too.
    public createContactFormTicket(data: ContactFormData): Observable<unknown> {
        const headers = this.getHeaders();
        const content = {
            subject: `Kontaktformular: ${data.topic?.label ?? this.getCustomerTypeForDisplay(data.customerType)}`,
            name: data.name,
            email: data.mail,
            description: `<h1>Anfrage über Kontaktformular: ${
                data.topic?.label ?? this.getCustomerTypeForDisplay(data.customerType)
            }</h1>
                <p><strong>Kundeninformationen:</strong></p>
                <p>
                    Name: ${data.name}<br>
                    Straße: ${data.address}<br>
                    Ort: ${data.city}<br>
                    Tel.: ${data.phone || 'Keine Angabe'}<br>
                </p>
                <p><strong>Nachricht:</strong><br></p>
                <p>
                    <pre>${data.msg || 'Keine Angaben'}</pre>
                </p>`,
            status: 2,
            priority: 1,
            group_id: data.topic ? data.topic.groupId : contactFormGroupIds.b2b,
            type: data.topic?.type ?? 'Kontaktformular',
            tags: data.tag ? [data.tag.name] : [],
        };
        const body = data.captchaResponse
            ? {
                  data: content,
                  captchaResponse: data.captchaResponse,
              }
            : content;
        return this.http.post(`${basePath}tickets`, body, { headers, notify: 'ALL' });
    }

    // note: if the data sent changes and we still use the php captcha proxy, then proxy
    // might need an update, too.
    public createTerminationOrRevocationTicket(data: ContractTerminationOrRevocationFormData): Observable<unknown> {
        const headers = this.getHeaders();
        const content = {
            subject: `Vertrag ${data.terminationOrRevocation === 'termination' ? 'kündigen' : 'widerrufen'}`,
            name: data.name,
            email: data.email,
            description: `<h1>Anfrage über Formular für Kündigung oder Widerruf: ${data.terminationOrRevocation === 'termination' ? 'Kündigung' : 'Widerruf'}</h1>
            <p><strong>Kundeninformation:</strong></p>
            <p>
                    Versicherungsnummer: ${data.insuranceNumber}<br>
                    Name: ${data.name}<br>
                    Straße: ${data.street || 'Keine Angabe'}<br>
                    Ort: ${data.city || 'Keine Angabe'}<br>
            </p>`,
            status: 2,
            priority: 1,
            group_id: contactFormGroupIds.termination,
            type: 'Kontaktformular',
            tags: [],
        };
        const body = data.captchaResponse
            ? {
                  data: content,
                  captchaResponse: data.captchaResponse,
              }
            : content;
        return this.http.post(`${basePath}tickets`, body, { headers, notify: 'ALL' });
    }

    private getCustomerTypeForDisplay(customerType: ContactFormCustomerType): string {
        switch (customerType) {
            case 'b2b':
                return 'Vertriebspartner / Geschäftskunde';
            case 'default':
            default:
                return 'Privat versichert';
        }
    }

    private getHeaders(): HttpHeaders {
        if (environment.freshdeskApiKey) {
            return new HttpHeaders().set('Authorization', `Basic ${base64Encode(environment.freshdeskApiKey)}:X`);
        }
        return new HttpHeaders();
    }
}
